import { useEffect, useState } from 'react';
import { probeWebGpu, type GpuSupport } from '@design-ai/gpu-core';
import { bootEngine, type Engine } from '../engine.js';

export interface EngineState {
  support: GpuSupport;
  engine: Engine | null;
  error: string | null;
}

/**
 * Boots the GPU engine once for the lifetime of the app.
 *
 * Not torn down on unmount: React 18+ mounts effects twice in development, and
 * destroying a `GPUDevice` that ORT is holding would take the runtime with it.
 */
export function useEngine(): EngineState {
  const [support] = useState<GpuSupport>(() => probeWebGpu());
  const [engine, setEngine] = useState<Engine | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!support.supported) return;
    let cancelled = false;
    bootEngine()
      .then((created) => {
        if (!cancelled) setEngine(created);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [support.supported]);

  return { support, engine, error };
}
